import { css, useTheme } from '@emotion/react';
import { useAtom } from 'jotai';
import * as React from 'react';

import { CategoryOption, Choice, DeliveryTime } from 'api/api';

import { useCategoryState } from '~src/category/category-state';
import { InlineExtensionList } from '~src/category/inline-extension-list';
import { openedAtom } from '~src/category/state/category-extesion-atoms';
import { constructUserChoice } from '~src/category/user-choice';
import { UserChoiceProvider } from '~src/category/user-choice-provider';
import { OpenListButtonLayout } from '~src/commons/components/button/open-list-button';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { useAriaGridCell } from '~src/ui/aria-grid';
import { ListState, useInlineListState } from '~src/ui/list-state';
import { ShippingCardUI } from '~src/ui/shipping-card';
import { useIpc } from '~src/utils/ipc-utils';

import {
  formatMainDeliveryTimePart,
  formatSecondaryDeliveryTimePart,
  getTimeSpanFromDeliveryTime,
} from './formatters';

interface TimeSlotListItemProps {
  deliveryTime: DeliveryTime;
  listState: ListState<DeliveryTime>;
  selectedOption: CategoryOption;
  onSelect: (choice: Choice) => void;
}

export const InlineTimeSlotListItem: React.FC<TimeSlotListItemProps> = ({
  deliveryTime,
  listState,
  selectedOption,
  onSelect,
}) => {
  const { tICU, formatDate, locale } = useTranslations();
  const isTomorrowHoliday = !!useGlobalState().additional?.is_tomorrow_holiday;
  const category = useCategoryState();

  const ref = React.useRef(null);

  const { cellProps, isSelected } = useAriaGridCell({
    itemKey: deliveryTime.id,
    listState,
    ref,
    enableAutoSelect: false,
    onSelect: itemKey => {
      // don't submit the same location
      if (itemKey === listState.selectedKey) return;

      onSelect(constructUserChoice(category, selectedOption, itemKey));
    },
  });

  const shouldRenderSecondaryDeliveryTime = deliveryTime.format === 'weekday_date';

  return (
    <ShippingCardUI
      // TODO ask design about placement of these
      headerExtensions={{
        subtitle: (
          <div
            css={css({
              display: 'flex',
              justifyContent: 'space-between',
            })}
          >
            {shouldRenderSecondaryDeliveryTime && (
              <span>{formatSecondaryDeliveryTimePart(deliveryTime.max, formatDate)}</span>
            )}
            <span>{getTimeSpanFromDeliveryTime(deliveryTime, tICU)}</span>
          </div>
        ),
      }}
      radioTitle={
        isSelected ? tICU(`widget.aria.SELECTED_DELIVERY_DATE`) : tICU(`widget.aria.DELIVERY_DATE`)
      }
      key={deliveryTime.id}
      selected={isSelected}
      title={
        formatMainDeliveryTimePart({
          deliveryTime,
          translate: tICU,
          dateFormatter: formatDate,
          locale,
          isTomorrowHoliday,
        })!
      }
      spacing="normal"
      textStyle="normal"
      enablePadding
      ariaLevel={4}
      innerRef={ref}
      {...cellProps}
    />
  );
};

interface InlineTimeSlotExtensionProps {
  timeText: string;
  selectedChoice: Choice;
  selectedOption: CategoryOption;
}

export const InlineTimeSlotExtension: React.FC<InlineTimeSlotExtensionProps> = ({
  timeText,
  selectedChoice,
  selectedOption,
}) => {
  const ipc = useIpc();
  const { tICU } = useTranslations();
  const listState = useInlineListState({
    items: selectedOption.delivery_times,
    getKey: (deliveryTime: DeliveryTime) => deliveryTime.id,
    defaultSelectedKey: selectedChoice.time_slot_ref,
  });
  const theme = useTheme();

  const [, setOpened] = useAtom(openedAtom);

  const buttonRef = React.useRef<HTMLDivElement>(null);

  return (
    <UserChoiceProvider>
      {({ onUpdate }) => (
        <OpenListButtonLayout
          onClick={() => setOpened(prev => !prev)}
          innerRef={buttonRef}
          title={{ text: tICU('widget.DELIVERY_DATE'), textStyle: 'normal', color: theme.black }}
          description={{
            text: timeText,
            textStyle: 'normal',
            color: theme.darkGrey,
          }}
        >
          <InlineExtensionList
            getId={item => item.id}
            listState={listState}
            listStyles={css({
              '&>[role=grid]>[role=row]:first-child>[role=gridcell]': {
                borderTop: 'none',
                paddingTop: '13px',
              },
            })}
          >
            {({ item, hidden }) =>
              hidden || (
                <InlineTimeSlotListItem
                  key={item.id}
                  deliveryTime={item}
                  listState={listState}
                  selectedOption={selectedOption}
                  onSelect={newChoice => {
                    onUpdate(newChoice);
                    setOpened(false);
                    if (buttonRef.current) {
                      ipc.send({
                        name: 'request_scroll_change',
                        data: buttonRef.current.getBoundingClientRect().top,
                      });
                    }
                  }}
                />
              )
            }
          </InlineExtensionList>
        </OpenListButtonLayout>
      )}
    </UserChoiceProvider>
  );
};
